import React, { FunctionComponent, ReactNode } from 'react'
import { Badge, BadgeProps, DesignLanguage, Grid, Link, Message, Skeleton, Text, useIsDesignLanguage, usePhoneNumber } from '@afterpaytouch/core'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { Country } from '@afterpaytouch/portal-api/types'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { editShippingAddressRoute, useNavigateToShippingAddress } from '../../config/router'
import { BASE_PATH } from '../../env'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useIsShippingOptionRequired } from '../../state'
import { Trans, useTranslation } from '../../utils/convergence'
import { useCheckoutColor } from '../../utils/color'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'

interface ShippingAddressItemProps {
  id?: number | string
  name?: string
  address1?: string
  address2?: string
  area2?: string
  suburb?: string
  state?: string
  postcode?: string
  phoneNumber?: string
  preferred?: boolean
  disabled?: boolean
  countryCode?: Country
  showIncompleteAddress?: boolean
  isAddressEmpty?: boolean
  isAddressLoading?: boolean
  boldNameLabel?: boolean
}

export const ShippingAddressItem: FunctionComponent<ShippingAddressItemProps> = (props) => {
  const {
    id,
    showIncompleteAddress = false,
    isAddressLoading = false,
    isAddressEmpty = false,
    name,
    address1,
    address2,
    area2,
    suburb,
    state,
    postcode,
    phoneNumber,
    preferred,
    countryCode,
    disabled = false,
    boldNameLabel = true,
  } = props
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const emptyBoxSrc = [BASE_PATH, 'images/empty-box.svg'].join('/')
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const router = useRouter()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const formatter = usePhoneNumber({ country: countryCode })
  const { subtextColor, headingTextColor, textColor } = useCheckoutColor({ isDisabled: disabled })
  const isIntegratedShipping = useIsShippingOptionRequired()
  const navigateToShippingAddress = useNavigateToShippingAddress()

  const displayIncompleteAddress = showIncompleteAddress && (typeof id === 'number' || typeof id === 'string')
  const badgeColour: BadgeProps['color'] = isArcadeDesignLanguage ? 'Light' : 'Black'

  const editShippingAddress = (): void => {
    logEvent(TrackingEvent.PRESSED_EDIT_SHIPPING_ADDRESS)
    router.push(editShippingAddressRoute(id))
  }

  const renderLocationDetails = (): JSX.Element => {
    const nameTextSize = isArcadeDesignLanguage ? smallTextSize : textSize
    const nameTextColor = isArcadeDesignLanguage ? textColor : headingTextColor

    if (isArcadeDesignLanguage) {
      return (
        <>
          <Text size={smallTextSize} color={textColor} bold testNameSpace='shipping-address-item-name'>
            {name}
          </Text>
          {preferred && (
            <Text size={smallTextSize} color={textColor} bold testNameSpace='shipping-address-item-preferred'>
              {t('shippingAddress:preferredLabel')}
            </Text>
          )}
          <Text size={smallTextSize} color={subtextColor} testNameSpace='shipping-address-item-inline-location-details'>
            {[address1, address2, area2, suburb, state, postcode].filter(Boolean).join(', ')}
          </Text>
        </>
      )
    }

    return (
      <>
        <Text size={nameTextSize} color={nameTextColor} bold={boldNameLabel} testNameSpace='shipping-address-item-name'>
          {name}
        </Text>
        <Text size={smallTextSize} color={subtextColor} testNameSpace='shipping-address-item-location-details'>
          {[address1, address2, area2, suburb, state, postcode].filter(Boolean).join(', ')}
        </Text>
      </>
    )
  }

  const renderAddressDetails = (): JSX.Element => {
    if (isArcadeDesignLanguage) {
      return (
        <div className={styles.wrapAddress}>
          {renderLocationDetails()}
          {phoneNumber && (
            <Text size={smallTextSize} color={subtextColor}>
              {formatter.formatInternational(phoneNumber)}
            </Text>
          )}
        </div>
      )
    }

    return (
      <Grid>
        <Grid.Item>
          <div className={styles.wrapAddress}>
            {renderLocationDetails()}
            {phoneNumber && (
              <Text size={smallTextSize} color={subtextColor}>
                {formatter.formatInternational(phoneNumber)}
              </Text>
            )}
          </div>
        </Grid.Item>
        {preferred && (
          <Grid.FixedItem width={80} align='right'>
            <Badge color={badgeColour} text='Preferred' disabled={false} />
          </Grid.FixedItem>
        )}
      </Grid>
    )
  }

  const renderEmptyAddressText = (): ReactNode => {
    if (isArcadeDesignLanguage) {
      return null
    }

    if (isArcadeDesignLanguage) {
      const onEditAddressClick = (): void => {
        logEvent(TrackingEvent.PRESSED_CHANGE_SHIPPING_ADDRESS)
        navigateToShippingAddress()
      }

      return (
        <Message
          kind='Success'
          iconName='Warning'
          heading={
            isIntegratedShipping
              ? t('shippingAddress:noChosenShippingAddressMessage_integrated.heading')
              : t('shippingAddress:noChosenShippingAddressMessage.heading')
          }
          description={
            <Text renderAs='span' size={smallTextSize}>
              <Trans
                components={{
                  shippingAddressLink: <Link onClick={onEditAddressClick} kind='Underline' />,
                }}
                i18nKey={
                  isIntegratedShipping
                    ? 'shippingAddress:noChosenShippingAddressMessage_integrated.description'
                    : 'shippingAddress:noChosenShippingAddressMessage.description'
                }
              />
            </Text>
          }
        />
      )
    }

    return (
      <Grid center gutter='none'>
        <Grid.FixedItem width={70}>
          <div className={styles.alignImage}>
            <Image
              src={emptyBoxSrc}
              alt=''
              width='40'
              height='48'
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </div>
        </Grid.FixedItem>
        <span className={styles.contentPadding}>
          <Text size={smallTextSize} color='Gray40' renderAs='div'>
            {isIntegratedShipping ? t('shippingAddress:noChosenShippingAddress_integrated') : t('shippingAddress:noChosenShippingAddress')}
          </Text>
        </span>
      </Grid>
    )
  }

  const renderAddressTextLoader = (): JSX.Element => {
    return (
      <div data-testid='shipping-address-loader'>
        <div className={styles.skeleton}>
          <Skeleton />
        </div>
        <div className={styles.skeleton}>
          <Skeleton />
        </div>
        <div className={styles.skeleton}>
          <Skeleton />
        </div>
      </div>
    )
  }

  const renderContent = (): ReactNode => {
    if (isAddressLoading) {
      return renderAddressTextLoader()
    }
    if (isAddressEmpty && !isAddressLoading) {
      return renderEmptyAddressText()
    }

    return renderAddressDetails()
  }

  const renderIncompleteAddress = (): JSX.Element => {
    if (isArcadeDesignLanguage) {
      return (
        <Text size={smallTextSize} color='Fire'>
          <Trans
            components={{ editAddress: <Link onClick={editShippingAddress} kind='Underline' /> }}
            i18nKey='shippingAddress:shippingAddressNotComplete_inline'
          />
        </Text>
      )
    }

    if (isArcadeDesignLanguage) {
      return (
        <Text size={smallTextSize} color='Fire'>
          {t('shippingAddress:shippingAddressNotComplete')}
        </Text>
      )
    }

    return (
      <Text size={smallTextSize} color='Fire'>
        <Link onClick={editShippingAddress} kind='Underline'>
          {t('shippingAddress:editAddress')}
        </Link>
      </Text>
    )
  }

  return (
    <MaskedElement actionName='Shipping Address'>
      <div className={styles.container}>
        {renderContent()}
        {displayIncompleteAddress && renderIncompleteAddress()}
      </div>
    </MaskedElement>
  )
}
